import type {
  InboxV2CommentConversationMessage,
  InboxV2CommentMessageKind,
  InboxV2AnyDirectConversationMessage,
  InboxV2ConversationMessage,
  InboxV2ConversationMessageKind,
  InboxV2CreatingConversationMessage,
  InboxV2DirectMessageKind,
  InboxV2InternalConversationMessage,
  InboxV2MessageAttachment,
  InboxV2MessageReaction,
  InboxV2Platform,
  InboxV2Sentiment,
  InboxV2SystemConversationMessage,
  InboxV2UnsupportedConversationMessage,
} from "../types";

export type InboxV2ConversationLayoutKind =
  | "comment"
  | "direct-message"
  | "review"
  | "system"
  | "internal"
  | "unsupported"
  | "unknown";

export type InboxV2CommentAction =
  "change-sentiment" | "delete" | "hide" | "send-direct-message";

export type InboxV2MessageSupportConfig = {
  actions?:
    | ReadonlyArray<InboxV2CommentAction>
    | ((
        message: InboxV2ConversationMessage
      ) => ReadonlyArray<InboxV2CommentAction>);
  loveReaction?: boolean;
  reactions?: boolean;
  sentimentDisplay?: boolean;
};

export type InboxV2MessageKindDefinition = {
  family: InboxV2ConversationLayoutKind;
  platform?: InboxV2Platform;
  layouts: ReadonlyArray<InboxV2ConversationLayoutKind>;
  support?: InboxV2MessageSupportConfig;
};

const commentMessageActions = [
  "change-sentiment",
  "send-direct-message",
  "hide",
  "delete",
] as const satisfies ReadonlyArray<InboxV2CommentAction>;

const tiktokCommentMessageActions = [
  "change-sentiment",
  "hide",
] as const satisfies ReadonlyArray<InboxV2CommentAction>;

const linkedInCommentMessageActions = [
  "change-sentiment",
] as const satisfies ReadonlyArray<InboxV2CommentAction>;

function getOwnThreadedCommentFilteredActions(
  actions: ReadonlyArray<InboxV2CommentAction>,
  message: InboxV2ConversationMessage
) {
  if (
    isInboxV2ThreadedCommentMessage(message) &&
    message.isConversationAccountSender
  ) {
    return actions.filter(
      (action) => action !== "send-direct-message" && action !== "hide"
    );
  }

  return actions;
}

function getThreadedCommentActions(
  message: InboxV2ConversationMessage
): ReadonlyArray<InboxV2CommentAction> {
  return getOwnThreadedCommentFilteredActions(commentMessageActions, message);
}

const commentMessageSupport = {
  actions: commentMessageActions,
  reactions: true,
  sentimentDisplay: true,
} as const satisfies InboxV2MessageSupportConfig;

const restrictedCommentMessageSupport = {
  actions: ["change-sentiment"],
  reactions: false,
  sentimentDisplay: true,
} as const satisfies InboxV2MessageSupportConfig;

const directMessageSupport = {
  loveReaction: true,
  reactions: true,
} as const satisfies InboxV2MessageSupportConfig;

const tiktokDirectMessageSupport = {
  reactions: true,
} as const satisfies InboxV2MessageSupportConfig;

const facebookDirectMessageSupport =
  {} as const satisfies InboxV2MessageSupportConfig;

export const inboxV2MessageKindDefinitions = {
  "fb-comment": {
    family: "comment",
    platform: "facebook",
    layouts: ["comment"],
    support: restrictedCommentMessageSupport,
  },
  "fb-threaded-comment": {
    family: "comment",
    platform: "facebook",
    layouts: ["comment"],
    support: {
      ...commentMessageSupport,
      actions: getThreadedCommentActions,
    },
  },
  "fb-mention-v2": {
    family: "comment",
    platform: "facebook",
    layouts: ["comment"],
    support: restrictedCommentMessageSupport,
  },
  "ig-comment": {
    family: "comment",
    platform: "instagram",
    layouts: ["comment"],
    support: restrictedCommentMessageSupport,
  },
  "ig-threaded-comment": {
    family: "comment",
    platform: "instagram",
    layouts: ["comment"],
    support: {
      ...commentMessageSupport,
      actions: getThreadedCommentActions,
    },
  },
  "ig-mention": {
    family: "comment",
    platform: "instagram",
    layouts: ["comment"],
    support: restrictedCommentMessageSupport,
  },
  "ig-mention-v2": {
    family: "comment",
    platform: "instagram",
    layouts: ["comment"],
    support: restrictedCommentMessageSupport,
  },
  "li-comment": {
    family: "comment",
    platform: "linkedin",
    layouts: ["comment"],
    support: {
      ...commentMessageSupport,
      actions: linkedInCommentMessageActions,
    },
  },
  "li-threaded-comment": {
    family: "comment",
    platform: "linkedin",
    layouts: ["comment"],
    support: {
      ...commentMessageSupport,
      actions: (message) =>
        getOwnThreadedCommentFilteredActions(
          linkedInCommentMessageActions,
          message
        ),
    },
  },
  "tt-threaded-comment": {
    family: "comment",
    platform: "tiktok",
    layouts: ["comment"],
    support: {
      ...commentMessageSupport,
      actions: tiktokCommentMessageActions,
      loveReaction: true,
    },
  },
  "gmb-review-reply": {
    family: "comment",
    platform: "google-business",
    layouts: ["review"],
  },
  "gmb-answer": {
    family: "comment",
    platform: "google-business",
    layouts: ["comment"],
    support: restrictedCommentMessageSupport,
  },
  "fb-message": {
    family: "direct-message",
    platform: "facebook",
    layouts: ["direct-message"],
    support: facebookDirectMessageSupport,
  },
  "ig-message": {
    family: "direct-message",
    platform: "instagram",
    layouts: ["direct-message"],
    support: directMessageSupport,
  },
  "tt-message": {
    family: "direct-message",
    platform: "tiktok",
    layouts: ["direct-message"],
    support: tiktokDirectMessageSupport,
  },
  "ig-story-mention": {
    family: "direct-message",
    platform: "instagram",
    layouts: ["direct-message"],
    support: directMessageSupport,
  },
  "ig-story-reply": {
    family: "direct-message",
    platform: "instagram",
    layouts: ["direct-message"],
    support: directMessageSupport,
  },
  "fb-post": {
    family: "unsupported",
    platform: "facebook",
    layouts: [],
  },
  "fb-mention-in-post-v2": {
    family: "unsupported",
    platform: "facebook",
    layouts: [],
  },
  "ig-post": {
    family: "unsupported",
    platform: "instagram",
    layouts: [],
  },
  "ig-mention-post": {
    family: "unsupported",
    platform: "instagram",
    layouts: [],
  },
  "ig-mention-in-post-v2": {
    family: "unsupported",
    platform: "instagram",
    layouts: [],
  },
  "li-post": {
    family: "unsupported",
    platform: "linkedin",
    layouts: [],
  },
  internal: {
    family: "internal",
    layouts: ["comment", "direct-message", "review", "internal"],
  },
  system: {
    family: "system",
    layouts: ["comment", "direct-message", "review", "system"],
  },
  typing: {
    family: "system",
    layouts: ["comment", "direct-message", "review", "system"],
  },
  "message-creating": {
    family: "system",
    layouts: ["comment", "direct-message", "review"],
  },
  unknown: {
    family: "unknown",
    layouts: ["unknown"],
  },
} as const satisfies Record<
  InboxV2ConversationMessageKind,
  InboxV2MessageKindDefinition
>;

export function inboxV2MessageKindSupportsLayout(
  kind: InboxV2ConversationMessageKind,
  layout: InboxV2ConversationLayoutKind
) {
  return (
    inboxV2MessageKindDefinitions[kind]
      .layouts as ReadonlyArray<InboxV2ConversationLayoutKind>
  ).includes(layout);
}

export function getInboxV2MessageKindSupportConfig(
  kind: InboxV2ConversationMessageKind
) {
  const definition: InboxV2MessageKindDefinition =
    inboxV2MessageKindDefinitions[kind];

  return definition.support ?? {};
}

function getInboxV2CommentActionsForMessage(
  message: InboxV2ConversationMessage
) {
  const actions = getInboxV2MessageKindSupportConfig(message.kind).actions;

  if (!actions) {
    return [];
  }

  const resolvedActions =
    typeof actions === "function" ? actions(message) : actions;

  return message.isConversationAccountSender
    ? resolvedActions.filter((action) => action !== "change-sentiment")
    : resolvedActions;
}

export function inboxV2MessageKindSupportsActions(
  kind: InboxV2ConversationMessageKind,
  message?: InboxV2ConversationMessage
) {
  if (message) {
    return getInboxV2CommentActionsForMessage(message).length > 0;
  }

  return Boolean(getInboxV2MessageKindSupportConfig(kind).actions);
}

export function inboxV2MessageKindSupportsLoveReaction(
  kind: InboxV2ConversationMessageKind
) {
  return Boolean(getInboxV2MessageKindSupportConfig(kind).loveReaction);
}

export function inboxV2MessageKindSupportsReactions(
  kind: InboxV2ConversationMessageKind
) {
  return Boolean(getInboxV2MessageKindSupportConfig(kind).reactions);
}

export function inboxV2MessageKindSupportsSentimentDisplay(
  kind: InboxV2ConversationMessageKind,
  message?: InboxV2ConversationMessage
) {
  if (message?.isConversationAccountSender) {
    return false;
  }

  return Boolean(getInboxV2MessageKindSupportConfig(kind).sentimentDisplay);
}

export function inboxV2MessageKindSupportsCommentAction(
  kind: InboxV2ConversationMessageKind,
  action: InboxV2CommentAction,
  message?: InboxV2ConversationMessage
) {
  if (message) {
    return getInboxV2CommentActionsForMessage(message).includes(action);
  }

  const actions = getInboxV2MessageKindSupportConfig(kind).actions;

  return Boolean(Array.isArray(actions) && actions.includes(action));
}

export function isInboxV2MessageKindRenderable(
  kind: InboxV2ConversationMessageKind
) {
  return inboxV2MessageKindDefinitions[kind].layouts.length > 0;
}

export function isInboxV2MessageRenderable(
  message: InboxV2ConversationMessage
) {
  return isInboxV2MessageKindRenderable(message.kind);
}

export function isInboxV2CommentMessageKind(
  kind: InboxV2ConversationMessageKind
): kind is InboxV2CommentMessageKind {
  return inboxV2MessageKindDefinitions[kind].family === "comment";
}

export function isInboxV2ThreadedCommentMessageKind(
  kind: InboxV2ConversationMessageKind
): kind is
  | "fb-threaded-comment"
  | "ig-threaded-comment"
  | "li-threaded-comment"
  | "tt-threaded-comment" {
  return (
    kind === "fb-threaded-comment" ||
    kind === "ig-threaded-comment" ||
    kind === "li-threaded-comment" ||
    kind === "tt-threaded-comment"
  );
}

export function isInboxV2DirectMessageKind(
  kind: InboxV2ConversationMessageKind
): kind is InboxV2DirectMessageKind {
  return inboxV2MessageKindDefinitions[kind].family === "direct-message";
}

export function isInboxV2SystemMessageKind(
  kind: InboxV2ConversationMessageKind
): kind is "system" | "typing" {
  return kind === "system" || kind === "typing";
}

export function getInboxV2MessagesForLayout(
  messages: ReadonlyArray<InboxV2ConversationMessage>,
  layout: InboxV2ConversationLayoutKind
) {
  return messages.filter((message) =>
    inboxV2MessageKindSupportsLayout(message.kind, layout)
  );
}

export function isInboxV2CommentMessage(
  message: InboxV2ConversationMessage
): message is InboxV2CommentConversationMessage {
  return isInboxV2CommentMessageKind(message.kind);
}

export function isInboxV2ThreadedCommentMessage(
  message: InboxV2ConversationMessage
): message is InboxV2CommentConversationMessage {
  return isInboxV2ThreadedCommentMessageKind(message.kind);
}

export function isInboxV2DirectMessage(
  message: InboxV2ConversationMessage
): message is InboxV2AnyDirectConversationMessage {
  return isInboxV2DirectMessageKind(message.kind);
}

export function isInboxV2InternalMessage(
  message: InboxV2ConversationMessage
): message is InboxV2InternalConversationMessage {
  return message.kind === "internal";
}

export function isInboxV2CreatingMessage(
  message: InboxV2ConversationMessage
): message is InboxV2CreatingConversationMessage {
  return message.kind === "message-creating";
}

export function isInboxV2InternalLikeMessage(
  message: InboxV2ConversationMessage
): boolean {
  return (
    isInboxV2InternalMessage(message) ||
    (isInboxV2CreatingMessage(message) && message.subKind === "internal")
  );
}

export function isInboxV2CreatingSocialMessage(
  message: InboxV2ConversationMessage
): message is InboxV2CreatingConversationMessage {
  return isInboxV2CreatingMessage(message) && message.subKind === "social";
}

export function isInboxV2SystemMessage(
  message: InboxV2ConversationMessage
): message is InboxV2SystemConversationMessage {
  return isInboxV2SystemMessageKind(message.kind);
}

export function getInboxV2MessageSentiment(
  message: InboxV2ConversationMessage | undefined
): InboxV2Sentiment | undefined {
  return message && "sentiment" in message ? message.sentiment : undefined;
}

const inboxV2PostSentimentMessageKinds = new Set<
  InboxV2UnsupportedConversationMessage["kind"]
>([
  "fb-post",
  "fb-mention-in-post-v2",
  "ig-post",
  "ig-mention-post",
  "ig-mention-in-post-v2",
  "li-post",
]);

export function isInboxV2PostSentimentMessage(
  message: InboxV2ConversationMessage
): message is InboxV2UnsupportedConversationMessage {
  return isInboxV2PostSentimentMessageKind(message.kind);
}

export function isInboxV2PostSentimentMessageKind(
  kind: InboxV2ConversationMessageKind
) {
  return inboxV2PostSentimentMessageKinds.has(
    kind as InboxV2UnsupportedConversationMessage["kind"]
  );
}

export function getInboxV2PostSentimentMessage(
  messages: InboxV2ConversationMessage[]
) {
  return messages.find((message) => isInboxV2PostSentimentMessage(message));
}

export function getInboxV2MessageStory(
  message: InboxV2ConversationMessage | undefined
): InboxV2MessageAttachment | undefined {
  return message && "story" in message ? message.story : undefined;
}

export function getInboxV2MessageAttachments(
  message: InboxV2ConversationMessage | undefined
): InboxV2MessageAttachment[] {
  return message && "attachments" in message ? (message.attachments ?? []) : [];
}

export function getInboxV2MessageReactions(
  message: InboxV2ConversationMessage
): InboxV2MessageReaction[] {
  return "reactions" in message ? (message.reactions ?? []) : [];
}
